import { Textarea } from '@/components/ui/textarea';
import { Lightbulb } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

interface CreativeContextInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  showSuggestions?: boolean;
}

export function CreativeContextInput({ value, onChange, disabled, showSuggestions = true }: CreativeContextInputProps) {
  const isMobile = useIsMobile();
  const { t, language } = useLanguage();

  const prompts = [
    { key: 'older', en: 'Make the person look 10 years older', es: 'Hacer que la persona se vea 10 años mayor' },
    { key: 'beach', en: 'Change the background to a beach sunset', es: 'Cambiar el fondo a un atardecer en la playa' },
    { key: 'lighting', en: 'Add dramatic lighting and shadows', es: 'Añadir iluminación y sombras dramáticas' },
    { key: 'logo', en: 'Keep the logo centered when extending', es: 'Mantener el logo centrado al extender' },
  ];
  
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-primary" />
        {t('creativeContext', 'label')} <span className="text-muted-foreground">{t('creativeContext', 'optional')}</span>
      </label>
      <Textarea
        placeholder={t('creativeContext', 'placeholder')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`bg-secondary/30 border-border focus:border-primary resize-none ${isMobile ? 'min-h-[78px]' : 'min-h-[60px]'}`}
      />
      {showSuggestions && (
        <div className="flex flex-wrap gap-1.5">
          {prompts.map((prompt) => (
            <button
              key={prompt.key}
              onClick={() => onChange(language === 'es' ? prompt.es : prompt.en)}
              disabled={disabled}
              className="text-xs px-2 py-1 bg-secondary rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors disabled:opacity-50"
            >
              {language === 'es' ? prompt.es : prompt.en}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
